/* eslint-disable react-refresh/only-export-components */
import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import ProtectedRoute from './routes/ProtectedRoute';
import ConsentRoute from './routes/ConsentRoute';
import ForcePasswordChangeRoute from './routes/ForcePasswordChangeRoute';
import PaymentRoute from './routes/PaymentRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Consent from './pages/Consent';
import ForcePasswordChange from './pages/ForcePasswordChange';
import PaymentRequired from './pages/PaymentRequired';
import GuardianConsent from './pages/public/GuardianConsent';
import GuardianConsentGroup from './pages/public/GuardianConsentGroup';
import AspirantSignup from './pages/AspirantSignup';
import RouteError from './components/shared/RouteError';
import { lazyWithReload } from './lib/chunkReload';

const AdminApp = lazyWithReload(() => import('./pages/admin/AdminApp'));
const MentorApp = lazyWithReload(() => import('./pages/mentor/MentorApp'));
const StudentApp = lazyWithReload(() => import('./pages/student/StudentApp'));
const NotFound = lazyWithReload(() => import('./components/shared/NotFound'));
const DiagnosticAssessment = lazyWithReload(() => import('./pages/student/DiagnosticAssessment'));
const PendingGuardianConsent = lazyWithReload(() => import('./pages/student/PendingGuardianConsent'));
const MockExamTaking = lazyWithReload(() => import('./pages/student/practice/MockExamTaking'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
    </div>
  );
}

export const router = createBrowserRouter([
  // Root pathless route: its errorElement catches errors from every page,
  // including those rendered by StudentApp/MentorApp/AdminApp's descendant
  // <Routes> (where errorElement is not supported). Without it React Router
  // shows its raw "Unexpected Application Error" developer screen.
  {
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      // Public guardian consent page — no auth (Story 3.10, AC3).
      { path: '/guardian-consent', element: <GuardianConsent /> },
      { path: '/guardian-consent/group', element: <GuardianConsentGroup /> },
      // Public aspirant self-signup — no auth (Story 10.2, AC1).
      { path: '/aspirant/signup', element: <AspirantSignup /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/consent', element: <Consent /> },
          { path: '/set-password', element: <ForcePasswordChange /> },
          { path: '/payment-required', element: <PaymentRequired /> },
          {
            element: <ForcePasswordChangeRoute />,
            children: [
              {
                element: <ProtectedRoute requiredRole="ADMIN" />,
                children: [
                  {
                    path: '/admin/*',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <AdminApp />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                element: <ProtectedRoute requiredRole="MENTOR" />,
                children: [
                  {
                    path: '/mentor/*',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <MentorApp />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                element: <ProtectedRoute requiredRole="STUDENT" />,
                children: [
                  // Minor "Account Pending Activation" screen — sits OUTSIDE
                  // ConsentRoute so a MINOR_PENDING student can reach it (Story 3.10).
                  {
                    path: '/pending-guardian-consent',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PendingGuardianConsent />
                      </Suspense>
                    ),
                  },
                  {
                    element: <ConsentRoute />,
                    children: [
                      // PaymentGuard on the backend resolves after consent, so the
                      // frontend mirrors that order: a MINOR_PENDING student sees the
                      // consent flow first, never a payment screen.
                      {
                        element: <PaymentRoute />,
                        children: [
                          // Shell-free exam focus mode — must precede the /student/* wildcard
                          // so React Router resolves it before falling into StudentApp's
                          // sidebar+topbar chrome.
                          // Using /session/:sessionId to avoid matching static sub-paths like /gate.
                          {
                            path: '/student/self-assessment/session/:sessionId',
                            element: (
                              <Suspense fallback={<PageLoader />}>
                                <DiagnosticAssessment />
                              </Suspense>
                            ),
                          },
                          // Shell-free full-screen mock-exam surfaces (decision #14,
                          // Stories 6.6/6.7) — taking + results render without StudentApp's
                          // sidebar+topbar chrome. Mounted here, ahead of the /student/*
                          // wildcard, exactly like the diagnostic focus-mode route above.
                          {
                            path: '/student/mock-exams/:examId/take',
                            element: (
                              <Suspense fallback={<PageLoader />}>
                                <MockExamTaking />
                              </Suspense>
                            ),
                          },
                          {
                            path: '/student/*',
                            element: (
                              <Suspense fallback={<PageLoader />}>
                                <StudentApp />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '/', element: <Navigate to="/login" replace /> },
      { path: '*', element: <Suspense fallback={<PageLoader />}><NotFound homePath="/login" homeLabel="Back to Login" /></Suspense> },
    ],
  },
]);
